/**
 * Protected Route Component — Redirects unauthenticated users to the SSO hub.
 * Optionally restricts by role or by an active CCCD national/regional team.
 */
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { redirectToSso } from '@alascorporation/api-client';
import { useAuthStore, isStaff, mustChangePassword } from '../../stores/authStore';
import { ssoConfig, GESTION_CHANGE_PASSWORD_URL } from '../../lib/sso';
import { useCccdEvents } from '../../hooks/useCccdEvents';

const CHANGE_PASSWORD_PATH = '/change-password';

interface Props {
  children: React.ReactNode;
  requiredRole?: 'student' | 'instructor' | 'admin';
  /** Restricts the route to users with at least one CCCD event (national or
   * regional). Staff (role !== 'student') always bypasses this check
   * regardless of team status — see academy-staff-unrestricted-access. Which
   * specific :eventId is valid for the viewer is checked by the page itself,
   * not here. */
  requiredTeamStatus?: 'any';
}

export default function ProtectedRoute({ children, requiredRole, requiredTeamStatus }: Props) {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();
  const events = useCccdEvents();

  const gatedStaff = mustChangePassword(user) && isStaff(user);

  useEffect(() => {
    if (!isAuthenticated) {
      redirectToSso(ssoConfig, location.pathname + location.search);
      return;
    }
    // Staff manage their password in Gestión, not Academy — a hard,
    // cross-origin redirect can't be done via <Navigate>. See
    // enforce-must-change-password-gate: this handles both a gate that was
    // already true at mount and one that flips true mid-session (Zustand
    // re-renders this on onMustChangePassword's setMustChangePassword call).
    if (gatedStaff) {
      window.location.href = GESTION_CHANGE_PASSWORD_URL;
    }
  }, [isAuthenticated, gatedStaff, location.pathname, location.search]);

  if (!isAuthenticated || gatedStaff) {
    return null;
  }

  // A pending mandatory password change blocks every other protected route —
  // see enforce-must-change-password-gate design.md Decision 4. Every
  // subsequent API call would 403 with MUST_CHANGE_PASSWORD anyway; this
  // redirect happens before that instead of after. Staff never reach this
  // branch (handled above, off to Gestión) — only students land on
  // Academy's own /change-password page.
  if (mustChangePassword(user) && location.pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} state={{ from: location.pathname + location.search }} replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredTeamStatus && !isStaff(user)) {
    if (events.isLoading) {
      return null;
    }
    const hasAnyEvent = events.nationalEvents.length > 0 || events.regionalEvents.length > 0;
    if (!hasAnyEvent) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
}
